export function initDroid(container) {
    const droidVideo = container.querySelector('#droid-video');
    const droidCanvas = container.querySelector('#droid-canvas');
    const droidCtx = droidCanvas.getContext('2d', {
        willReadFrequently: true,
    });

    // Set video source only if not already set to prevent reloading
    if (!droidVideo.src || !droidVideo.src.includes('droid.mp4')) {
        droidVideo.src = '/droid.mp4';
        droidVideo.muted = true;
        droidVideo.load();
    } else if (droidVideo.readyState === 0) {
        // If source is set but not loaded, just load it
        droidVideo.load();
    }

    // Animation state
    let isPlayingForward = true;
    const frameRate = 1/30;
    let lastTime = 0;

    // Color settings for the green screen
    const colorThreshold = 150;
    const similarityThreshold = 100;

    // Add dialogue box to the container
    let dialogueBox = document.createElement('div');
    dialogueBox.id = 'dialogue-box';
    dialogueBox.style.cssText = `
    background: rgba(72, 221, 72, 0.6);
    color: rgb(255, 248, 156);
    font-family: monospace;
    border-radius: 5px;
    height: fit-content;
    max-width: 320px;
    font-size: 1.1vh;
    `;
    container.appendChild(dialogueBox);

    // Add a separate container for buttons
    let buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
        display: flex;
        gap: 8px;
        justify-content: flex-start;
    `;
    dialogueBox.appendChild(buttonContainer);

    let currentAnimation = null;

    // Add new variables for eye blinking
    let isEyeGlowing = false;
    let eyeGlowIntensity = 0;

    // Add new variables for eye effects
    let eyeColor = { r: 255, g: 0, b: 0 }; // Default red
    let eyeAnimationType = 'none';
    let eyeAnimationStartTime = 0;

    // Replace the static EYE_POSITION with a function to detect the eye
    function detectEyePosition(imageData) {
        const data = imageData.data;
        const width = imageData.width;
        let maxRedIntensity = 0;
        let eyeX = 0;
        let eyeY = 0;
        
        // Scan the upper portion of the droid where we expect the eye to be
        const scanStartY = Math.floor(imageData.height * 0.1);  // Start 10% from top
        const scanEndY = Math.floor(imageData.height * 0.4);    // Scan until 40% of height
        
        for (let y = scanStartY; y < scanEndY; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4;
                const red = data[i];
                const green = data[i + 1];
                const blue = data[i + 2];
                
                // Look for pixels that are predominantly red
                if (red > 150 && red > green * 1.5 && red > blue * 1.5) {
                    const redIntensity = red - (green + blue) / 2;
                    if (redIntensity > maxRedIntensity) {
                        maxRedIntensity = redIntensity;
                        eyeX = x;
                        eyeY = y;
                    }
                }
            }
        }
        
        return {
            x: eyeX,
            y: eyeY,
            radius: 15,  // You might want to adjust this based on the detected eye size
            intensity: maxRedIntensity
        };
    }

    function setEyeColor(r, g, b) {
        eyeColor = { r, g, b };
        // Optional: Add immediate visual feedback
        if (!isEyeGlowing) {
            isEyeGlowing = true;
            eyeGlowIntensity = 100;
            setTimeout(() => {
                if (eyeAnimationType === 'none') {
                    isEyeGlowing = false;
                    eyeGlowIntensity = 0;
                }
            }, 100);
        }
    }

    function addButton(text, onClick) {
        console.log('Adding button:', text);
        const button = document.createElement('button');
        button.textContent = text;
        button.style.cssText = `
            padding: 5px 10px;
            border-radius: 4px;
            border: 1px solid rgb(44, 255, 242);
            background: transparent;
            color: rgb(44, 255, 242);
            cursor: pointer;
            font-family: monospace;
            opacity: 0;
            transition: all 0.3s ease;
            transform: translateY(10px);
            margin: 5px;
        `;
        button.addEventListener('click', onClick);
        buttonContainer.appendChild(button);
        
        // Trigger fade in
        requestAnimationFrame(() => {
            button.style.opacity = '1';
            button.style.transform = 'translateY(0)';
        });

        // Add hover styles
        button.addEventListener('mouseover', () => {
            button.style.background = 'rgba(255, 255, 255, 0.2)';
            button.style.transform = 'scale(1.05)';
        });
        
        button.addEventListener('mouseout', () => {
            button.style.background = 'transparent';
            button.style.transform = 'scale(1)';
        });

        return button;
    }

    // Add a variable to track the current input's resolve function
    let currentInputResolve = null;

    function setCurrentInputResolve(resolve) {
        currentInputResolve = resolve;
    }

    function clearDialogue() {
        // Resolve any pending input promise
        if (currentInputResolve) {
            currentInputResolve();
            currentInputResolve = null;
        }

        // Stop any ongoing text animation
        if (currentAnimation) {
            clearTimeout(currentAnimation);
            currentAnimation = null;
        }

        // Remove any active input elements and their event listeners
        const inputContainer = dialogueBox.querySelector('div:has(input)');
        if (inputContainer) {
            const input = inputContainer.querySelector('input');
            const submitButton = inputContainer.querySelector('button');
            
            // Remove event listeners
            input.replaceWith(input.cloneNode(true));
            submitButton.replaceWith(submitButton.cloneNode(true));
        }

        // Immediately remove all content
        dialogueBox.innerHTML = '';

        // Hide the dialogue box
        dialogueBox.style.display = 'none';

        // Recreate the button container
        buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            gap: 8px;
            justify-content: flex-start;
        `;
        dialogueBox.appendChild(buttonContainer);

        return this;
    }

    function addText(text, duration = 1.5) {
        // Clear any existing animation timeout
        if (currentAnimation) {
            clearTimeout(currentAnimation);
        }
        
        dialogueBox.style.display = 'block';
        
        // Create a text container if it doesn't exist
        let textContainer = dialogueBox.querySelector('.text-container');
        if (!textContainer) {
            textContainer = document.createElement('div');
            textContainer.className = 'text-container';
            dialogueBox.insertBefore(textContainer, buttonContainer);
        }
        
        textContainer.textContent = '';
        
        // Replace <nl> with actual newline character
        text = text.replace(/<nl>/g, '\n');

        //count the number of newlines
        const newLineCount = (text.match(/\n/g) || []).length;
        if(newLineCount > 2){
            textContainer.style.lineHeight = 'normal';
        } else {
            textContainer.style.lineHeight = '22px';
        }

        
        const totalChars = text.length;
        const timePerChar = (duration * 1000) / totalChars;
        let currentChar = 0;

        function revealNextChar() {
            if (currentChar < totalChars) {
                if (text[currentChar] === '\n') {
                    textContainer.appendChild(document.createElement('br'));
                } else {
                    textContainer.appendChild(document.createTextNode(text[currentChar]));
                }
                currentChar++;
                currentAnimation = setTimeout(revealNextChar, timePerChar);
            }
        }

        revealNextChar();
        return this;
    }

    // Add a single animation loop state variable
    let animationFrameId = null;

    function animate(timestamp) {
        // Calculate precise time delta
        const deltaTime = timestamp - lastTime;
        
        // Only update video time at our target frame rate
        if (deltaTime >= (frameRate * 1000)) {
            // Ensure we move exactly one frame worth of time
            const timeChange = frameRate;
            
            if (isPlayingForward) {
                if (droidVideo.currentTime >= droidVideo.duration - frameRate) {
                    droidVideo.currentTime = droidVideo.duration - frameRate;
                    isPlayingForward = false;
                } else {
                    droidVideo.currentTime += timeChange;
                }
            } else {
                if (droidVideo.currentTime <= frameRate) {
                    droidVideo.currentTime = 0;
                    isPlayingForward = true;
                } else {
                    droidVideo.currentTime -= timeChange;
                }
            }
            
            lastTime = timestamp;
        }

        // Process frame and eye animations
        processFrame();

        // Handle eye animations based on type
        if (eyeAnimationType === 'blink') {
            // Blink animation logic
            if (!eyeAnimationStartTime) eyeAnimationStartTime = timestamp;
            const progress = timestamp - eyeAnimationStartTime;
            const duration = 500; // blink duration
            
            if (progress < duration / 2) {
                eyeGlowIntensity = (progress / (duration / 2)) * 100;
            } else if (progress < duration) {
                eyeGlowIntensity = (1 - ((progress - duration / 2) / (duration / 2))) * 100;
            } else {
                isEyeGlowing = false;
                eyeGlowIntensity = 0;
                eyeAnimationType = 'none';
            }
        } else if (eyeAnimationType === 'pulse') {
            const duration = 2000;
            const progress = (timestamp - eyeAnimationStartTime) % duration;
            const phase = (Math.sin(progress / duration * Math.PI * 2) + 1) / 2;
            eyeGlowIntensity = phase * 100;
        } else if (eyeAnimationType === 'rainbow') {
            const duration = 3000;
            const progress = (timestamp - eyeAnimationStartTime) % duration;
            const hue = (progress / duration) * 360;
            
            // Convert HSL to RGB
            const h = hue / 360;
            let r, g, b;

            if (h < 1/3) {
                r = 1 - h * 3;
                g = h * 3;
                b = 0;
            } else if (h < 2/3) {
                r = 0;
                g = 2 - h * 3;
                b = h * 3 - 1;
            } else {
                r = h * 3 - 2;
                g = 0;
                b = 3 - h * 3;
            }

            eyeColor = {
                r: Math.round(r * 255),
                g: Math.round(g * 255),
                b: Math.round(b * 255)
            };
            eyeGlowIntensity = 100;
        } else if (eyeAnimationType === 'strobe') {
            const duration = 100;
            const progress = timestamp - eyeAnimationStartTime;
            eyeGlowIntensity = progress % (duration * 2) < duration ? 100 : 0;
        }

        animationFrameId = requestAnimationFrame(animate);
    }

    // Update the video loaded event listener
    droidVideo.addEventListener('loadeddata', () => {
        console.log('Loaded droid video');
        animationFrameId = requestAnimationFrame(animate);
    });

    function processFrame() {
        droidCtx.clearRect(0, 0, droidCanvas.width, droidCanvas.height);
        droidCtx.drawImage(droidVideo, -160, -80, droidVideo.videoWidth, droidVideo.videoHeight);
        
        // Always process green screen
        const fullImageData = droidCtx.getImageData(0, 0, droidCanvas.width, droidCanvas.height);
        const data = fullImageData.data;
        
        // Apply green screen effect to full canvas
        for (let i = 0; i < data.length; i += 4) {
            const green = data[i + 1];
            if (green > colorThreshold && 
                green > data[i] + similarityThreshold && 
                green > data[i + 2] + similarityThreshold) {
                data[i + 3] = 0;
            }
        }
        droidCtx.putImageData(fullImageData, 0, 0);

        // Only process eye effects if needed
        if (!isEyeGlowing) {
            return;
        }

        const eyePosition = detectEyePosition(fullImageData);
        if (!eyePosition || eyePosition.intensity === 0) return;

        // Process eye glow in smaller region
        const radius = eyePosition.radius;
        const region = {
            x: Math.max(0, eyePosition.x - radius),
            y: Math.max(0, eyePosition.y - radius),
            width: Math.min(droidCanvas.width - eyePosition.x + radius, radius * 2),
            height: Math.min(droidCanvas.height - eyePosition.y + radius, radius * 2)
        };

        const eyeRegionData = droidCtx.getImageData(region.x, region.y, region.width, region.height);
        const eyeData = eyeRegionData.data;

        // Pre-calculate constants for eye effect
        const eyeRadiusSquared = radius * radius;
        const intensityFactor = eyeGlowIntensity / 100;
        const rEffect = eyeColor.r * intensityFactor;
        const gEffect = eyeColor.g * intensityFactor;
        const bEffect = eyeColor.b * intensityFactor;

        // Apply eye glow effect
        for (let i = 0; i < eyeData.length; i += 4) {
            const x = (i / 4) % region.width + region.x - eyePosition.x;
            const y = Math.floor((i / 4) / region.width) + region.y - eyePosition.y;
            const distanceSquared = x * x + y * y;

            if (distanceSquared < eyeRadiusSquared && eyeData[i + 3] !== 0) {
                const glowFalloff = 1 - (distanceSquared / eyeRadiusSquared);
                eyeData[i] = Math.min(255, eyeData[i] + (rEffect * glowFalloff));
                eyeData[i + 1] = Math.min(255, eyeData[i + 1] + (gEffect * glowFalloff));
                eyeData[i + 2] = Math.min(255, eyeData[i + 2] + (bEffect * glowFalloff));
            }
        }

        droidCtx.putImageData(eyeRegionData, region.x, region.y);
    }
    
    function processFrameOld() {

        droidCtx.clearRect(0, 0, droidCanvas.width, droidCanvas.height);

        droidCtx.drawImage(droidVideo, -160, -80, droidVideo.videoWidth, droidVideo.videoHeight);
        
        // Only process image data if we need to (green screen or glowing eye)
        const imageData = droidCtx.getImageData(0, 0, droidCanvas.width, droidCanvas.height);
        const data = imageData.data;
        
        // Detect eye position only if glowing
        const eyePosition = isEyeGlowing ? detectEyePosition(imageData) : null;
        
        // Pre-calculate eye radius squared to avoid Math.sqrt later
        const eyeRadiusSquared = eyePosition ? eyePosition.radius * eyePosition.radius : 0;

        for (let i = 0; i < data.length; i += 4) {
            const red = data[i];
            const green = data[i + 1];
            const blue = data[i + 2];

            // Green screen effect
            if (green > colorThreshold && 
                green > red + similarityThreshold && 
                green > blue + similarityThreshold) {
                data[i + 3] = 0;
                continue; // Skip eye glow calculation for transparent pixels
            }

            // Only process eye glow if active and we have valid eye position
            if (isEyeGlowing && eyePosition && eyePosition.intensity > 0) {
                const x = (i / 4) % droidCanvas.width;
                const y = Math.floor((i / 4) / droidCanvas.width);
                
                // Use squared distance comparison instead of Math.sqrt
                const dx = x - eyePosition.x;
                const dy = y - eyePosition.y;
                const distanceSquared = dx * dx + dy * dy;

                if (distanceSquared < eyeRadiusSquared) {
                    // Use squared distances for falloff calculation
                    const glowFalloff = 1 - (distanceSquared / eyeRadiusSquared);
                    const intensity = eyeGlowIntensity * glowFalloff;
                    
                    data[i] = Math.min(255, data[i] + (eyeColor.r * intensity / 100));
                    data[i + 1] = Math.min(255, data[i + 1] + (eyeColor.g * intensity / 100));
                    data[i + 2] = Math.min(255, data[i + 2] + (eyeColor.b * intensity / 100));
                }
            }
        }

        droidCtx.putImageData(imageData, 0, 0);
    }

    // Remove the updateVideoTime function as it's now part of animate()

    // Update the eye animation functions to just set up state
    function blinkEye(duration = 500) {
        isEyeGlowing = true;
        eyeAnimationType = 'blink';
        eyeAnimationStartTime = null;
        return this;
    }

    function pulseEye(duration = 2000) {
        isEyeGlowing = true;
        eyeAnimationType = 'pulse';
        eyeAnimationStartTime = performance.now();
        return this;
    }

    function rainbowEye() {
        isEyeGlowing = true;
        eyeAnimationType = 'rainbow';
        eyeAnimationStartTime = performance.now();
        return this;
    }

    function strobeEye() {
        isEyeGlowing = true;
        eyeAnimationType = 'strobe';
        eyeAnimationStartTime = performance.now();
        return this;
    }

    function stopEyeAnimation() {
        isEyeGlowing = false;
        eyeGlowIntensity = 0;
        eyeAnimationType = 'none';
        return this;
    }

    // Add cleanup function to stop animation
    function cleanup() {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        return this;
    }

    function addInput(submitBtnText, id, onSubmit, defaultValue = '', placeholder = '') {
        const inputContainer = document.createElement('div');
        inputContainer.style.cssText = `
            display: flex;
            gap: 8px;
            margin-top: 10px;
            padding: 5px;
        `;

        const input = document.createElement('input');
        input.type = 'text';
        input.id = id;
        if (defaultValue) input.value = defaultValue;
        if (placeholder) input.placeholder = placeholder;
        input.style.cssText = `
            background: transparent;
            border: 1px solid white;
            border-radius: 4px;
            color: white;
            padding: 5px 10px;
            font-family: monospace;
            flex-grow: 1;
        `;

        const submitButton = document.createElement('button');
        submitButton.textContent = submitBtnText;
        submitButton.style.cssText = `
            padding: 5px 10px;
            border-radius: 4px;
            border: 1px solid white;
            background: transparent;
            color: white;
            cursor: pointer;
            font-family: monospace;
        `;

        submitButton.addEventListener('mouseover', () => {
            submitButton.style.background = 'rgba(255, 255, 255, 0.2)';
        });
        
        submitButton.addEventListener('mouseout', () => {
            submitButton.style.background = 'transparent';
        });

        submitButton.addEventListener('click', () => {
            const value = input.value.trim();
            if (value) {
                inputContainer.remove();
                onSubmit(value);
            }
        });

        input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                const value = input.value.trim();
                if (value) {
                    inputContainer.remove();
                    onSubmit(value);
                }
            }
        });

        inputContainer.appendChild(input);
        inputContainer.appendChild(submitButton);
        dialogueBox.appendChild(inputContainer);
        input.focus();
    }

    function addCopyableInput(text, id) {
        const inputContainer = document.createElement('div');
        inputContainer.style.cssText = `
            display: flex;
            gap: 8px;
            margin-top: 10px;
            padding: 5px;
        `;

        const input = document.createElement('input');
        input.type = 'text';
        input.id = id;
        input.value = text;
        input.readOnly = true;
        input.style.cssText = `
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid white;
            border-radius: 4px;
            color: white;
            padding: 5px 10px;
            font-family: monospace;
            flex-grow: 1;
            cursor: text;
        `;

        const copyButton = document.createElement('button');
        copyButton.textContent = 'Copy';
        copyButton.style.cssText = `
            padding: 5px 10px;
            border-radius: 4px;
            border: 1px solid rgb(44, 255, 242);
            background: transparent;
            color: rgb(44, 255, 242);
            cursor: pointer;
            font-family: monospace;
        `;

        copyButton.addEventListener('mouseover', () => {
            copyButton.style.background = 'rgba(44, 255, 242, 0.2)';
        });
        
        copyButton.addEventListener('mouseout', () => {
            copyButton.style.background = 'transparent';
        });

        copyButton.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(text);
                const originalText = copyButton.textContent;
                copyButton.textContent = 'Copied!';
                copyButton.style.color = '#00ff00';
                setTimeout(() => {
                    copyButton.textContent = originalText;
                    copyButton.style.color = 'rgb(44, 255, 242)';
                }, 2000);
            } catch (err) {
                // Fallback for older browsers
                input.select();
                document.execCommand('copy');
                const originalText = copyButton.textContent;
                copyButton.textContent = 'Copied!';
                copyButton.style.color = '#00ff00';
                setTimeout(() => {
                    copyButton.textContent = originalText;
                    copyButton.style.color = 'rgb(44, 255, 242)';
                }, 2000);
            }
        });

        inputContainer.appendChild(input);
        inputContainer.appendChild(copyButton);
        dialogueBox.appendChild(inputContainer);
        input.select();
    }

    // Return control functions
    return {
        addText,
        clearDialogue,
        addButton,
        addInput,
        addCopyableInput,
        setCurrentInputResolve,
        blinkEye,
        pulseEye,
        rainbowEye,
        strobeEye,
        setEyeColor,
        stopEyeAnimation,
        cleanup
    };
}
